import { useTextePublic } from '../lib/textes-publics'
/**
 * Fenêtre modale bâtie sur l’élément natif `<dialog>` : le navigateur fournit
 * le piège de focus, l’inertie de l’arrière-plan et la touche `Échap`. Rien à
 * réimplémenter, rien à mal implémenter.
 */

import { type ReactNode, useEffect, useId, useRef } from 'react'

import { cn } from '../lib/cn'

export type TailleModal = 'sm' | 'md' | 'lg' | 'xl'

const TAILLES: Readonly<Record<TailleModal, string>> = {
  sm: 'max-w-md',
  md: 'max-w-xl',
  lg: 'max-w-3xl',
  xl: 'max-w-6xl',
}

export interface ModalProps {
  readonly ouvert: boolean
  readonly onFermer: () => void
  readonly titre: ReactNode
  readonly libelleFermer?: string | undefined
  readonly description?: ReactNode | undefined
  readonly taille?: TailleModal | undefined
  /** Barre d’actions en pied de modale. */
  readonly pied?: ReactNode | undefined
  /** Un clic sur le fond ferme la modale. Désactivé pour les formulaires longs. */
  readonly fermetureExterieure?: boolean | undefined
  readonly className?: string | undefined
  readonly children: ReactNode
}

export function Modal({
  ouvert,
  onFermer,
  titre,
  libelleFermer,
  description,
  taille = 'md',
  pied,
  fermetureExterieure = true,
  className,
  children,
}: ModalProps) {
  const texteInterface = useTextePublic()
  const reference = useRef<HTMLDialogElement>(null)
  const idTitre = useId()
  const idDescription = `${idTitre}-description`

  useEffect(() => {
    const dialogue = reference.current
    if (dialogue === null) return

    if (ouvert && !dialogue.open) {
      // `showModal` n’existe pas dans jsdom : repli sur l’attribut `open`.
      if (typeof dialogue.showModal === 'function') dialogue.showModal()
      else dialogue.setAttribute('open', '')
    } else if (!ouvert && dialogue.open) {
      dialogue.close()
    }
  }, [ouvert])

  // La page de fond ne défile pas sous la modale.
  useEffect(() => {
    if (!ouvert) return undefined
    const precedent = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = precedent
    }
  }, [ouvert])

  return (
    <dialog
      ref={reference}
      aria-labelledby={idTitre}
      aria-describedby={description === undefined ? undefined : idDescription}
      className={cn(
        'm-auto w-[calc(100vw-2rem)] rounded-carte bg-white p-0 text-slate-900 shadow-2xl',
        'backdrop:bg-slate-900/50 backdrop:backdrop-blur-[1px]',
        'open:animate-glissement',
        TAILLES[taille],
        className,
      )}
      onCancel={(evenement) => {
        // On garde la main sur la fermeture pour que l’état React suive.
        evenement.preventDefault()
        onFermer()
      }}
      onKeyDown={(evenement) => {
        if (evenement.key === 'Escape') {
          evenement.preventDefault()
          onFermer()
        }
      }}
      onClick={(evenement) => {
        if (fermetureExterieure && evenement.target === reference.current) onFermer()
      }}
    >
      <div className="flex max-h-[85vh] flex-col">
        <header className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
          <div className="min-w-0">
            <h2 id={idTitre} className="text-base font-semibold">
              {titre}
            </h2>
            {description !== undefined && (
              <p id={idDescription} className="mt-1 text-sm text-slate-500">
                {description}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onFermer}
            aria-label={libelleFermer ?? texteInterface('Fermer')}
            // Cible tactile de 44 px au doigt, resserrée à la souris.
            className="-m-2 rounded p-3 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 sm:-m-1 sm:p-1"
          >
            <svg
              viewBox="0 0 20 20"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              className="size-5"
            >
              <path d="m5 5 10 10M15 5 5 15" strokeLinecap="round" />
            </svg>
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>

        {pied !== undefined && (
          <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3">
            {pied}
          </footer>
        )}
      </div>
    </dialog>
  )
}
